// Figma: node 18034:1401 — "Resident Action Feed / Campaigns / At a glance / Alerts".
// LOGIC NOTE: in LC2 these four sections (FeedComponent, CampaignsTable, Glance,
// DataAlerts) are all rendered stacked on the same page (see PortfolioTab.js) — this nav
// isn't a tab switcher, it's an in-page anchor row. Reproduced the same way here:
// clicking scrolls to the matching section. "At a glance" (Glance/GlanceSection.js) is now
// ported (see ../DashboardPage.js / Communities/CommunitySection.js), so that link is live.
import React, {useEffect, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {tokens} from '../../themes/ThemeCustomization';

const SECTIONS = [
    {id: 'resident-action-feed', label: 'Resident Action Feed'},
    {id: 'at-a-glance', label: 'At a glance'},
    {id: 'campaigns', label: 'Campaigns'},
    {id: 'alerts', label: 'Alerts'}
];

const TOP_HEADER_HEIGHT = 57; // sticky TopHeader
const NAV_HEIGHT = 57; // this sticky nav row (py 16 + text + 1px border)
const HEADER_OFFSET = TOP_HEADER_HEIGHT + NAV_HEIGHT + 16; // both sticky bars + breathing room

const SectionNav = ({active: initialActive = 'resident-action-feed'}) => {
    const [active, setActive] = useState(initialActive);

    // Scroll-spy: highlight the section currently crossing the upper part of the viewport.
    useEffect(() => {
        const visible = new Set();
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => (entry.isIntersecting ? visible.add(entry.target.id) : visible.delete(entry.target.id)));
                const current = SECTIONS.find((section) => visible.has(section.id));
                if (current) setActive(current.id);
            },
            {rootMargin: `-${HEADER_OFFSET}px 0px -60% 0px`}
        );
        SECTIONS.forEach((section) => {
            const el = document.getElementById(section.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const scrollTo = (id) => {
        setActive(id);
        const el = document.getElementById(id);
        if (!el) return;
        // The sticky top header (56px) would otherwise cover the section heading.
        const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({top: Math.max(0, top), behavior: 'smooth'});
    };

    return (
        <Stack
            direction="row"
            sx={{
                borderBottom: `1px solid ${tokens.color.neutral200}`,
                position: 'sticky',
                top: TOP_HEADER_HEIGHT,
                zIndex: 9,
                backgroundColor: '#fafafa'
            }}
        >
            {SECTIONS.map((section) => {
                const isActive = section.id === active;
                return (
                    <Box
                        key={section.id}
                        component="button"
                        type="button"
                        disabled={section.disabled}
                        onClick={() => scrollTo(section.id)}
                        sx={{
                            border: 'none',
                            background: 'none',
                            px: 1.5,
                            py: 2,
                            cursor: section.disabled ? 'default' : 'pointer',
                            position: 'relative'
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{color: section.disabled ? tokens.color.neutral300 : isActive ? tokens.color.sky600 : tokens.color.neutral600}}
                        >
                            {section.label}
                        </Typography>
                        {isActive && (
                            <Box sx={{position: 'absolute', bottom: '-1px', left: 0, width: '100%', height: '2px', backgroundColor: tokens.color.sky600}} />
                        )}
                    </Box>
                );
            })}
        </Stack>
    );
};

export default SectionNav;
